import { ModalWindow } from '@/components/UI/Common/ModalWindow';
import { Color } from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const TEXT_COLORS = [
  { label: 'Чёрный', value: '#111827' },
  { label: 'Серый', value: '#6b7280' },
  { label: 'Красный', value: '#dc2626' },
  { label: 'Оранжевый', value: '#ea580c' },
  { label: 'Зелёный', value: '#16a34a' },
  { label: 'Синий', value: '#2563eb' },
  { label: 'Фиолетовый', value: '#7c3aed' },
] as const;

function normalizeHexColor(color: string | undefined): string {
  if (!color?.trim()) return '#111827';
  const c = color.trim();
  if (/^#[0-9a-fA-F]{6}$/i.test(c)) return c;
  if (/^#[0-9a-fA-F]{3}$/i.test(c) && c.length === 4) {
    return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
  }
  const m = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    const h = (n: string) => Number(n).toString(16).padStart(2, '0');
    return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
  }
  return '#111827';
}

function currentBlockTextAlign(editor: Editor): string | null {
  const attrs = editor.state.selection.$from.parent.attrs as { textAlign?: string | null };
  const v = attrs.textAlign;
  return v == null || v === '' ? null : v;
}

function isSafeMessageLinkHref(href: string): boolean {
  const t = href.trim();
  if (!t) return false;
  if (t.startsWith('#')) return true;
  if (t.startsWith('/') && !t.startsWith('//')) return true;
  try {
    const u = new URL(t);
    return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:';
  } catch {
    return false;
  }
}

type AdminMessageRichEditorProps = {
  value: string;
  onChange: (html: string) => void;
  hasError?: boolean;
  disabled?: boolean;
};

function ToolbarButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded px-2 py-1 text-sm font-medium transition-colors disabled:opacity-40 ${
        active ? 'bg-gray-300 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function LinkDialog({
  isOpen,
  initialUrl,
  onClose,
  onApply,
}: {
  isOpen: boolean;
  initialUrl: string;
  onClose: () => void;
  onApply: (url: string) => void;
}) {
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setUrl(initialUrl);
    setError('');
  }, [isOpen, initialUrl]);

  if (!isOpen) return null;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = url.trim();
    if (trimmed === '') {
      onApply('');
      return;
    }
    if (!isSafeMessageLinkHref(trimmed)) {
      setError('Допустимы ссылки http(s), mailto, якорь #… или путь, начинающийся с /');
      return;
    }
    onApply(trimmed);
  };

  return createPortal(
    <ModalWindow isActive={isOpen} onSetActive={onClose} className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="grid gap-4">
        <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
          Ссылка
        </h2>
        <div>
          <label htmlFor="link-url" className="mb-1 block text-sm font-medium text-gray-700">
            Адрес ссылки
          </label>
          <input
            id="link-url"
            type="text"
            autoFocus
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError('');
            }}
            placeholder="https://…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">Оставьте пустым, чтобы убрать ссылку</p>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Отмена
          </button>
          <button
            type="submit"
            className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-medium text-white hover:brightness-110"
          >
            Применить
          </button>
        </div>
      </form>
    </ModalWindow>,
    document.body,
  );
}

function EditorToolbar({ editor, toolbarDisabled }: { editor: Editor; toolbarDisabled: boolean }) {
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const blockAlign = currentBlockTextAlign(editor);
  const linkInitialUrl = (editor.getAttributes('link').href as string | undefined) ?? 'https://';

  const handleApplyLink = (url: string) => {
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setIsLinkDialogOpen(false);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <ToolbarButton
          active={editor.isActive('bold')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Жирный
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Курсив
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('strike')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          Зачёркнутый
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          Подчёркнутый
        </ToolbarButton>
        <span className="mx-1 hidden h-6 self-stretch w-px bg-gray-300 sm:block" aria-hidden />
        <span className="flex flex-wrap items-center gap-1 text-xs text-gray-600">
          <span className="mr-0.5 shrink-0">Цвет:</span>
          {TEXT_COLORS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              title={label}
              aria-label={label}
              disabled={toolbarDisabled}
              onClick={() => editor.chain().focus().setColor(value).run()}
              className="h-6 w-6 shrink-0 rounded border border-gray-300 hover:ring-2 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ backgroundColor: value }}
            />
          ))}
          <label
            className={`ml-0.5 flex items-center gap-1 ${toolbarDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
          >
            <span className="sr-only">Свой цвет</span>
            <input
              type="color"
              disabled={toolbarDisabled}
              className="h-7 w-8 cursor-pointer rounded border border-gray-300 bg-white p-0 disabled:cursor-not-allowed"
              value={normalizeHexColor(
                editor.getAttributes('textStyle').color as string | undefined,
              )}
              onInput={(e) => {
                const v = (e.target as HTMLInputElement).value;
                editor.chain().focus().setColor(v).run();
              }}
            />
          </label>
          <ToolbarButton
            active={false}
            disabled={toolbarDisabled}
            onClick={() => editor.chain().focus().unsetColor().run()}
          >
            Сброс цвета
          </ToolbarButton>
        </span>
        <span className="mx-1 hidden h-6 self-stretch w-px bg-gray-300 sm:block" aria-hidden />
        <ToolbarButton
          active={editor.isActive('bulletList')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          Маркеры
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          Нумерация
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('blockquote')}
          disabled={toolbarDisabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          Цитата
        </ToolbarButton>
        <span className="mx-1 hidden h-6 self-stretch w-px bg-gray-300 sm:block" aria-hidden />
        <ToolbarButton
          active={editor.isActive('link')}
          disabled={toolbarDisabled}
          onClick={() => setIsLinkDialogOpen(true)}
        >
          Ссылка
        </ToolbarButton>
        <ToolbarButton
          active={false}
          disabled={toolbarDisabled || !editor.isActive('link')}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          Убрать ссылку
        </ToolbarButton>
        <span className="mx-1 hidden h-6 self-stretch w-px bg-gray-300 sm:block" aria-hidden />
        <span className="flex flex-wrap items-center gap-1 text-xs text-gray-600">
          <span className="mr-0.5 shrink-0">Выравнивание:</span>
          <ToolbarButton
            active={blockAlign == null || blockAlign === 'left'}
            disabled={toolbarDisabled}
            onClick={() => editor.chain().focus().unsetTextAlign().run()}
          >
            Влево
          </ToolbarButton>
          <ToolbarButton
            active={blockAlign === 'center'}
            disabled={toolbarDisabled}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            По центру
          </ToolbarButton>
          <ToolbarButton
            active={blockAlign === 'right'}
            disabled={toolbarDisabled}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            Вправо
          </ToolbarButton>
          <ToolbarButton
            active={blockAlign === 'justify'}
            disabled={toolbarDisabled}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            По ширине
          </ToolbarButton>
        </span>
      </div>
      <LinkDialog
        isOpen={isLinkDialogOpen}
        initialUrl={linkInitialUrl}
        onClose={() => setIsLinkDialogOpen(false)}
        onApply={handleApplyLink}
      />
    </>
  );
}

export function AdminMessageRichEditor({
  value,
  onChange,
  hasError,
  disabled,
}: AdminMessageRichEditorProps) {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
        validate: (href) => isSafeMessageLinkHref(href),
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: null,
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'flex min-h-[112px] max-w-none flex-col gap-3 px-3 py-2 outline-none [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-700 [&_blockquote>p]:mb-2 [&_blockquote>p]:last:mb-0 [&_li]:my-0.5 [&_li>p]:min-h-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:min-h-[1em] [&_ul]:list-disc [&_ul]:pl-6',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const borderClass = hasError
    ? 'border-red-500 focus-within:ring-red-500'
    : 'border-gray-300 focus-within:ring-blue-500';

  if (!editor) {
    return (
      <div
        className={`min-h-[160px] w-full animate-pulse rounded-lg border border-solid border-gray-200 bg-gray-50 ${borderClass}`}
      />
    );
  }

  return (
    <div
      className={`w-full overflow-hidden rounded-lg border border-solid transition-colors focus-within:ring-2 ${borderClass}`}
    >
      <EditorToolbar editor={editor} toolbarDisabled={!!disabled} />
      <EditorContent editor={editor} />
    </div>
  );
}
